import { useCallback, useRef } from 'react';
import { useChatContext } from '../store/ChatContext';
import {
  BotAgentStage,
  CartPreview,
  CheckoutReady,
  ProductDetailsPayload,
  SuggestedReply,
  UIMessage,
} from '../types/chat.types';

/**
 * Full bot:done payload — sideband fields are populated only when the matching
 * tool ran in this turn. See sky360-ms LLMStreamChunk for the wire contract.
 */
interface BotDonePayload {
  metadata: any;
  searchResults?: { tool: string; results: any[]; items?: any[] };
  product_details?: ProductDetailsPayload;
  cart_preview?: CartPreview;
  checkout_ready?: CheckoutReady;
  suggested_replies?: SuggestedReply[];
  current_stage?: BotAgentStage;
}

export interface UseChatSocketOptions {
  namespace: string;           // e.g. '/bot-agent'
  onDone?: (metadata: any) => Promise<void>;
  /** Fired when the BE migrates this session into another channel (e.g. WhatsApp). */
  onHandedOff?: (sessionId: string, to: string) => void;
}

export function useChatSocket({
  namespace,
  onDone,
  onHandedOff,
}: UseChatSocketOptions) {
  const { dispatch } = useChatContext();
  const socketRef = useRef<any>(null);
  const lastToolRef = useRef<string | null>(null);

  const connect = useCallback(async () => {
    if (socketRef.current) return;
    dispatch({ type: 'SET_SOCKET_STATUS', status: 'connecting' });

    try {
      const { io } = await import('socket.io-client');
      const baseUrl = process.env.REACT_APP_MS_BASE_URL ?? '';
      const token = localStorage.getItem('agent-admin-token');

      const socket = io(`${baseUrl}${namespace}`, {
        auth: token ? { token } : undefined,
        transports: ['websocket'],
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      socket.on('connect', () => dispatch({ type: 'SET_SOCKET_STATUS', status: 'connected' }));
      socket.on('disconnect', () => dispatch({ type: 'SET_SOCKET_STATUS', status: 'disconnected' }));
      socket.on('connect_error', () => dispatch({ type: 'SET_SOCKET_STATUS', status: 'error' }));

      socket.on('bot:chunk', ({ text }: { text: string }) => {
        dispatch({ type: 'APPEND_CHUNK', text });
      });

      socket.on('bot:searching', ({ tool, label }: { tool: string; label: string }) => {
        lastToolRef.current = tool;
        dispatch({ type: 'SET_TOOL_LABEL', label });
      });

      socket.on('bot:done', async (payload: BotDonePayload) => {
        dispatch({
          type: 'FLUSH_MESSAGE',
          sideband: {
            searchResults: payload.searchResults,
            productDetails: payload.product_details,
            cartPreview: payload.cart_preview,
            checkoutReady: payload.checkout_ready,
            suggestedReplies: payload.suggested_replies,
            currentStage: payload.current_stage,
          },
        });
        if (onDone) await onDone({ ...payload.metadata, lastTool: lastToolRef.current });
        lastToolRef.current = null;
      });

      socket.on(
        'session.handed_off',
        ({ session_id, to }: { session_id: string; to: string }) => {
          if (onHandedOff) onHandedOff(session_id, to);
        },
      );

      socket.on('bot:error', ({ message }: { message: string }) => {
        dispatch({ type: 'FLUSH_MESSAGE' });
        const errMsg: UIMessage = {
          id: crypto.randomUUID(),
          role: 'system',
          text: `⚠️ ${message}`,
          timestamp: new Date().toISOString(),
        };
        dispatch({ type: 'ADD_MESSAGE', message: errMsg });
      });

      socketRef.current = socket;
    } catch (err) {
      console.error('[ChatSocket] connect error', err);
      dispatch({ type: 'SET_SOCKET_STATUS', status: 'error' });
    }
  }, [dispatch, namespace, onDone, onHandedOff]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    dispatch({ type: 'SET_SOCKET_STATUS', status: 'disconnected' });
  }, [dispatch]);

  const emit = useCallback((event: string, payload: Record<string, any>) => {
    if (!socketRef.current) return;
    socketRef.current.emit(event, payload);
  }, []);

  return { connect, disconnect, emit };
}
